import { createContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { usePost } from 'hooks';
import {
  performSearch,
  setSearchRequest,
  performExternalSearch,
  performSearchListAssignedToResearchers,
  setListAssignedToResearchers,
  setGeoSearchData,
  setOrgInsightsSearchData,
  performSearchListAssignedToOrganisations,
  setListAssignedToOrganisatinos,
  setFilter,
  performOrgInsightsSearch,
} from 'actions/search';
import {
  getExternalSearch,
  getFilter,
  getOrganisationIds,
  getResearcherIds,
  getSearchRequest,
  getSearchView,
} from 'selectors/search';
import { OrganisationSearch } from './Results/helpers';

import SearchArea from './SearchArea';
import Statistics from './Statistics';
import Filters from './Filters';
import Results from './Results';
import RefreshModal from './RefreshModal';
import { ResultsArea } from './styled';
import { organisationsLists, researchersLists } from 'services/api';
import FundingSortModal from './FundingSortModal/fundingSortModal';
import {
  searchResultsReceived,
  setShowNLPFilterModal,
  setNLPModalState,
  NLP_STAGE,
} from 'actions/searchNLP';
import { getActiveAiSessionId } from 'selectors/searchNLP';
import { readSciAiRedirectParams } from 'components/SearchNLP/orgRedirectContract';

export const SearchContext = createContext();

const Search = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const view = useSelector(getSearchView);
  const activeAiSessionId = useSelector(getActiveAiSessionId);
  const [searchState, postSearch] = usePost({
    configuration: {
      onRequestSuccess: response => {
        dispatch(
          searchResultsReceived({
            sessionId: activeAiSessionId,
            response,
            view,
          })
        );
      },
    },
  });
  const [geoSearchState, postGeoSearch] = usePost({});
  const [orgInsightsSearchState, postOrgInsightsSearch] = usePost({});

  const [
    searchListAssignedToResearcher,
    postSearchListAssignedToResearcher,
  ] = usePost({ url: researchersLists });

  const [
    searchListAssignedToOrganisation,
    postSearchListAssignedOrganisation,
  ] = usePost({ url: organisationsLists });

  const [
    loadingListResearcherInformation,
    setLoadingListResearcherInformation,
  ] = useState(false);

  const externalSearch = useSelector(getExternalSearch);
  const lstResearchersIds = useSelector(getResearcherIds);
  const getRefreshSearch = useSelector(getFilter('refreshSearch'));
  const lstOrganisationsIds = useSelector(getOrganisationIds);
  const orgSearch = useSelector(getSearchRequest);
  const insightsFetchedFor = useRef(null);

  // SCIL-18101: arrived from an org profile's SciSearch AI modal via
  // ?sciAiQuery=... — auto-open the NLP modal prefilled with the query. Strip
  // the param so a refresh doesn't re-trigger. Runs once on mount.
  useEffect(() => {
    const params = readSciAiRedirectParams(window.location.search);
    if (!params) return;
    dispatch(setShowNLPFilterModal(true));
    dispatch(setNLPModalState({ query: params.query, stage: NLP_STAGE.INPUT }));
    history.replace(window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadListGroupByResearcher = () => {
    if (!loadingListResearcherInformation) {
      setLoadingListResearcherInformation(true);
      dispatch(
        performSearchListAssignedToResearchers(
          postSearchListAssignedToResearcher
        )
      );
    }
  };

  if (Object.keys(externalSearch).length > 0) {
    dispatch(performExternalSearch(postSearch, postGeoSearch, externalSearch));
  }

  useEffect(() => {
    if (searchState.res || searchState.error || searchState.loading) {
      dispatch(setSearchRequest(searchState));
    }
  }, [searchState, dispatch]);

  useEffect(() => {
    if (getRefreshSearch) {
      dispatch(performSearch(postSearch, postGeoSearch));
      dispatch(setFilter('refreshSearch', false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRefreshSearch]);

  useEffect(() => {
    if (geoSearchState.res || geoSearchState.error || geoSearchState.loading) {
      dispatch(setGeoSearchData(geoSearchState));
    }
  }, [geoSearchState, dispatch]);

  useEffect(() => {
    if (
      orgInsightsSearchState.res ||
      orgInsightsSearchState.error ||
      orgInsightsSearchState.loading
    ) {
      dispatch(setOrgInsightsSearchData(orgInsightsSearchState));
    }
  }, [orgInsightsSearchState, dispatch]);

  // Researcher/activity searches write the session's SciSearch insights to S3 inline; org search
  // does not — only the org insights endpoint does. In an AI session, fetch them eagerly once the
  // search lands (rather than lazily on the Insights tab) so the follow-up panel has insights to
  // read. Keyed on the result object so it fires once per search, not on every render.
  useEffect(() => {
    if (!OrganisationSearch(view) || !activeAiSessionId || orgSearch.loading)
      return;
    if (!(orgSearch.res?.totalResults > 0)) return;
    if (insightsFetchedFor.current === orgSearch.res) return;
    insightsFetchedFor.current = orgSearch.res;
    dispatch(performOrgInsightsSearch(postOrgInsightsSearch));
  }, [view, activeAiSessionId, orgSearch, dispatch, postOrgInsightsSearch]);

  useEffect(() => {
    if (lstResearchersIds) {
      dispatch(
        performSearchListAssignedToResearchers(
          postSearchListAssignedToResearcher
        )
      );
    }
  }, [lstResearchersIds, dispatch, postSearchListAssignedToResearcher]);

  useEffect(() => {
    if (lstOrganisationsIds) {
      dispatch(
        performSearchListAssignedToOrganisations(
          postSearchListAssignedOrganisation
        )
      );
    }
  }, [lstOrganisationsIds, dispatch, postSearchListAssignedOrganisation]);

  useEffect(() => {
    if (
      searchListAssignedToResearcher.res != null ||
      searchListAssignedToResearcher.error ||
      searchListAssignedToResearcher.loading
    ) {
      setLoadingListResearcherInformation(false);
      dispatch(setListAssignedToResearchers(searchListAssignedToResearcher));
    }
  }, [searchListAssignedToResearcher, dispatch]);

  useEffect(() => {
    if (
      searchListAssignedToOrganisation.res != null ||
      searchListAssignedToOrganisation.error ||
      searchListAssignedToOrganisation.loading
    ) {
      dispatch(
        setListAssignedToOrganisatinos(searchListAssignedToOrganisation)
      );
    }
  }, [searchListAssignedToOrganisation, dispatch]);

  return (
    <>
      <SearchContext.Provider
        value={{
          postSearch,
          postGeoSearch,
          postOrgInsightsSearch,
          reloadListGroupByResearcher,
        }}
      >
        <SearchArea />
        <Statistics />
        <ResultsArea>
          <Filters />
          <Results />
        </ResultsArea>
        <RefreshModal />
        <FundingSortModal />
      </SearchContext.Provider>
    </>
  );
};

export default Search;
